import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    Card,
    InputAdornment
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    Mic as MicIcon,
    Stop as StopIcon,
    CameraAlt as CameraAltIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, EMPLOYEE_STORE, STATUS_DRAFT, STATUS_SUBMITTED } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import CameraCapture from '../../shared/CameraCapture';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import { changeOrderService } from '../../../services/changeOrderService';
import useDictation from '../../../hooks/useDictation';

const ChangeOrderForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const uuid = searchParams.get('uuid');

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        jobCode: '',
        jobName: '',
        date: dayjs().format('YYYY-MM-DD'),
        customerId: '',
        items: [{
            id: generateUUID(),
            description: '',
            laborDetail: '',
            materialDetail: '',
            equipmentDetail: '',
            // Estimate fields
            units: '1',
            laborHours: '',
            laborRate: '',
            material: '',
            equipment: '',
            estimate: '0',
            customerName: '',
            customerSignature: ''
        }],
        photos: [],
        submittedBy: userFullName,
        submittedById: userId,
        submittedBySignature: '',
        status: STATUS_DRAFT
    });

    const [errors, setErrors] = useState({});
    const [jobsLoading, setJobsLoading] = useState(true);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [currentSignatureField, setCurrentSignatureField] = useState(null);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [dictationItemIndex, setDictationItemIndex] = useState(null);
    const [dictationField, setDictationField] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const sigPadRef = useRef(null);
    const saveTimeoutRef = useRef(null);

    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
        onError: (error) => {
            console.error('Dictation error:', error);
        }
    });

    const loadJobs = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(JOB_STORE, 'readonly');
            const store = tx.objectStore(JOB_STORE);
            const allJobs = await store.getAll();
            const sortedJobs = (allJobs || []).sort((a, b) => {
                const jobA = a.Job || '';
                const jobB = b.Job || '';
                return jobB.localeCompare(jobA);
            });
            setJobs(sortedJobs);
        } catch (error) {
            console.error('Error loading jobs:', error);
        } finally {
            setJobsLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const allEmployees = await store.getAll();
            setEmployees(allEmployees || []);
        } catch (error) {
            console.error('Error loading employees:', error);
        }
    };

    const loadFormData = async () => {
        if (!uuid) return;

        try {
            const data = await changeOrderService.getFromIndexedDB(uuid);
            if (data) {
                // For photos loaded from IndexedDB, ensure they have proper display URLs
                if (data.photos) {
                    data.photos = data.photos.map(photo => {
                        if (!photo.dataUrl && photo.filename) {
                            photo.url = `/api/change-order/${uuid}/photo/${photo.filename}`;
                        }
                        return photo;
                    });
                }
                setForm(data);
                setSubmitted(data.status === STATUS_SUBMITTED);
            }
        } catch (error) {
            console.error('Error loading form data:', error);
        }
    };

    useEffect(() => {
        loadJobs();
        loadEmployees();
    }, []);

    useEffect(() => {
        if (uuid) {
            loadFormData();
        }
    }, [uuid]);

    const queueSave = (updatedForm) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await changeOrderService.saveToIndexedDB(updatedForm);
            } catch (error) {
                console.error('Error saving to IndexedDB:', error);
            }
        }, 500);
    };

    const handleJobChange = (event, newValue) => {
        const updated = {
            ...form,
            jobCode: newValue?.Job || '',
            jobName: newValue?.JobName || ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Calculate estimate: Units × (Labor hours × Labor Rate + Material + Equipment)
    const calculateEstimate = (item) => {
        const units = parseFloat(item.units) || 0;
        const laborHours = parseFloat(item.laborHours) || 0;
        const laborRate = parseFloat(item.laborRate) || 0;
        const material = parseFloat(item.material) || 0;
        const equipment = parseFloat(item.equipment) || 0;

        const estimate = units * (laborHours * laborRate + material + equipment);
        return estimate.toFixed(2);
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = form.items.map((item, i) => {
            if (i === index) {
                const updatedItem = { ...item, [field]: value };
                // Recalculate estimate if any of the estimate fields change
                if (['units', 'laborHours', 'laborRate', 'material', 'equipment'].includes(field)) {
                    updatedItem.estimate = calculateEstimate(updatedItem);
                }
                return updatedItem;
            }
            return item;
        });
        const updated = { ...form, items: updatedItems };
        setForm(updated);
        queueSave(updated);
    };

    const addItem = () => {
        const newItem = {
            id: generateUUID(),
            description: '',
            laborDetail: '',
            materialDetail: '',
            equipmentDetail: '',
            // Estimate fields
            units: '1',
            laborHours: '',
            laborRate: '',
            material: '',
            equipment: '',
            estimate: '0',
            customerName: '',
            customerSignature: ''
        };
        const updated = { ...form, items: [...form.items, newItem] };
        setForm(updated);
        queueSave(updated);
    };

    const removeItem = (index) => {
        if (form.items.length === 1) return;
        const updated = { ...form, items: form.items.filter((_, i) => i !== index) };
        setForm(updated);
        queueSave(updated);
    };

    // Photo handlers
    const handlePhotoCapture = (photoData) => {
        const newPhoto = {
            dataUrl: photoData.dataUrl,
            url: photoData.dataUrl,
            name: photoData.name,
            description: '',
            timestamp: photoData.timestamp || new Date().toISOString()
        };
        const updated = { ...form, photos: [...(form.photos || []), newPhoto] };
        setForm(updated);
        queueSave(updated);
        setCameraOpen(false);
    };

    const updatePhotoDescription = (index, desc) => {
        const updatedPhotos = (form.photos || []).map((photo, i) =>
            i === index ? { ...photo, description: desc } : photo
        );
        const updated = { ...form, photos: updatedPhotos };
        setForm(updated);
        queueSave(updated);
    };

    const removePhoto = (index) => {
        const updated = { ...form, photos: (form.photos || []).filter((_, i) => i !== index) };
        setForm(updated);
        queueSave(updated);
    };

    const toggleDictation = async (itemIndex, fieldName) => {
        if (!dictationSupported) {
            console.error('Speech recognition not supported in this browser');
            return;
        }

        if (isListening && dictationItemIndex === itemIndex && dictationField === fieldName) {
            stopDictation();
            setDictationItemIndex(null);
            setDictationField(null);
        } else {
            if (isListening) {
                stopDictation();
            }
            setDictationItemIndex(itemIndex);
            setDictationField(fieldName);
            await startDictation({
                onResult: (transcript) => {
                    if (!transcript) return;
                    setForm(currentForm => {
                        const currentText = currentForm.items[itemIndex]?.[fieldName] || '';
                        const needsSpace = currentText && !currentText.endsWith(' ');
                        const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                        const updatedItems = currentForm.items.map((item, i) =>
                            i === itemIndex ? { ...item, [fieldName]: newText } : item
                        );
                        const updated = { ...currentForm, items: updatedItems };
                        queueSave(updated);
                        return updated;
                    });
                },
                onError: (e) => {
                    console.error('Recognition error:', e?.error || e);
                }
            });
        }
    };

    const handleSignatureAccept = (signatureData) => {
        let signature = signatureData;
        if (typeof signatureData !== 'string') {
            signature = sigPadRef.current?.toDataURL() || '';
        }

        if (currentSignatureField === 'submittedBy') {
            const updated = {
                ...form,
                submittedBySignature: signature
            };
            setForm(updated);
            queueSave(updated);
        } else if (currentSignatureField?.startsWith('customer')) {
            const index = parseInt(currentSignatureField.replace('customer', ''));
            const updatedItems = form.items.map((item, i) =>
                i === index ? { ...item, customerSignature: signature } : item
            );
            const updated = { ...form, items: updatedItems };
            setForm(updated);
            queueSave(updated);
        }

        setSignatureModalOpen(false);
        setCurrentSignatureField(null);
    };

    const handleEmployeeSelect = (employee) => {
        if (employee) {
            const updated = {
                ...form,
                submittedBy: employee.FullName || employee.name || '',
                submittedById: employee.id || ''
            };
            setForm(updated);
            queueSave(updated);
        }
        setEmployeeModalOpen(false);
    };

    const handleSubmit = async () => {
        const newErrors = {};

        if (!form.jobCode) {
            newErrors.jobCode = 'Job is required';
        }

        if (!form.date) {
            newErrors.date = 'Date is required';
        }

        if (form.items.length === 0) {
            newErrors.items = 'At least one item is required';
        }

        if (!form.submittedBySignature) {
            newErrors.submittedBySignature = 'Your signature is required';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            Swal.fire({
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Form?',
            text: 'Once submitted, the form cannot be edited',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it'
        });

        if (!result.isConfirmed) return;

        setErrors({});
        setSaving(true);

        try {
            // Clean photos for submission
            const cleanedPhotos = (form.photos || []).map(photo => {
                if (photo.dataUrl) {
                    // New photo — send dataUrl for server to save
                    return {
                        dataUrl: photo.dataUrl,
                        description: photo.description || '',
                        timestamp: photo.timestamp
                    };
                }
                // Existing photo — send only filename and description
                return {
                    filename: photo.filename,
                    description: photo.description || '',
                    timestamp: photo.timestamp
                };
            });

            const submitData = {
                ...form,
                photos: cleanedPhotos,
                status: STATUS_SUBMITTED
            };

            // Submit to backend
            await changeOrderService.submit(submitData);

            // Delete from IndexedDB after successful submission
            await changeOrderService.deleteFromIndexedDB(form.uuid);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Change Order submitted successfully'
            });

            navigate('/operations/change-order');
        } catch (error) {
            console.error('Error submitting form:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to submit form'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/operations/change-order')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Change Order / Extra Work</Typography>
                    </Box>
                </Box>

                {/* Job, Date, Customer ID in Header */}
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            options={jobs}
                            getOptionLabel={(option) => `${option.Job || ''} - ${option.JobName || ''}`}
                            value={jobs.find(j => j.Job === form.jobCode) || null}
                            onChange={handleJobChange}
                            loading={jobsLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Job"
                                    size="small"
                                    error={!!errors.jobCode}
                                    helperText={errors.jobCode}
                                    required
                                />
                            )}
                            disabled={submitted}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Date"
                            value={form.date || ''}
                            onChange={(e) => handleFieldChange('date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            error={!!errors.date}
                            helperText={errors.date}
                            required
                            disabled={submitted}
                            onClick={(e) => {
                                if (e.target.showPicker) {
                                    e.target.showPicker();
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Customer ID"
                            value={form.customerId || ''}
                            onChange={(e) => handleFieldChange('customerId', e.target.value)}
                            disabled={submitted}
                            placeholder="Optional"
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                <Grid container spacing={2}>
                    {/* Repeatable Items */}
                    {form.items.map((item, index) => (
                        <Grid item xs={12} key={item.id}>
                            <Card variant="outlined" sx={{ p: 2, position: 'relative' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Item #{index + 1}
                                    </Typography>
                                    {form.items.length > 1 && !submitted && (
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeItem(index)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Box>

                                <Grid container spacing={2}>
                                    {/* Description */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Description"
                                            value={item.description || ''}
                                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                            multiline
                                            rows={2}
                                            disabled={submitted}
                                            InputProps={{
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleDictation(index, 'description')}
                                                            color={isListening && dictationItemIndex === index && dictationField === 'description' ? 'error' : 'primary'}
                                                        >
                                                            {isListening && dictationItemIndex === index && dictationField === 'description' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Labor Detail, Material Detail, Equipment Detail in one row */}
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Labor Detail"
                                            value={item.laborDetail || ''}
                                            onChange={(e) => handleItemChange(index, 'laborDetail', e.target.value)}
                                            multiline
                                            minRows={1}
                                            maxRows={6}
                                            disabled={submitted}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    padding: '0px',
                                                    alignItems: 'center'
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8.5px 14px'
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleDictation(index, 'laborDetail')}
                                                            color={isListening && dictationItemIndex === index && dictationField === 'laborDetail' ? 'error' : 'primary'}
                                                        >
                                                            {isListening && dictationItemIndex === index && dictationField === 'laborDetail' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Material Detail"
                                            value={item.materialDetail || ''}
                                            onChange={(e) => handleItemChange(index, 'materialDetail', e.target.value)}
                                            multiline
                                            minRows={1}
                                            maxRows={6}
                                            disabled={submitted}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    padding: '0px',
                                                    alignItems: 'center'
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8.5px 14px'
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleDictation(index, 'materialDetail')}
                                                            color={isListening && dictationItemIndex === index && dictationField === 'materialDetail' ? 'error' : 'primary'}
                                                        >
                                                            {isListening && dictationItemIndex === index && dictationField === 'materialDetail' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Equipment Detail"
                                            value={item.equipmentDetail || ''}
                                            onChange={(e) => handleItemChange(index, 'equipmentDetail', e.target.value)}
                                            multiline
                                            minRows={1}
                                            maxRows={6}
                                            disabled={submitted}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    padding: '0px',
                                                    alignItems: 'center'
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: '8.5px 14px'
                                                }
                                            }}
                                            InputProps={{
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleDictation(index, 'equipmentDetail')}
                                                            color={isListening && dictationItemIndex === index && dictationField === 'equipmentDetail' ? 'error' : 'primary'}
                                                        >
                                                            {isListening && dictationItemIndex === index && dictationField === 'equipmentDetail' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* Estimate Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                                            Estimate
                                        </Typography>
                                    </Grid>

                                    {/* Estimate Fields in one row */}
                                    <Grid item xs={12} sm={2.4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Units"
                                            value={item.units || ''}
                                            onChange={(e) => handleItemChange(index, 'units', e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal', step: '0.01', min: '0' }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={2.4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Labor Hours"
                                            value={item.laborHours || ''}
                                            onChange={(e) => handleItemChange(index, 'laborHours', e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal', step: '0.01', min: '0' }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={2.4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Labor Rate"
                                            value={item.laborRate || ''}
                                            onChange={(e) => handleItemChange(index, 'laborRate', e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal', step: '0.01', min: '0' }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={2.4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Material"
                                            value={item.material || ''}
                                            onChange={(e) => handleItemChange(index, 'material', e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal', step: '0.01', min: '0' }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={2.4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label="Equipment"
                                            value={item.equipment || ''}
                                            onChange={(e) => handleItemChange(index, 'equipment', e.target.value)}
                                            disabled={submitted}
                                            inputProps={{ inputMode: 'decimal', step: '0.01', min: '0' }}
                                        />
                                    </Grid>

                                    {/* Estimate Total, Customer Name and Customer Signature in one row */}
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Estimate Total"
                                            value={item.estimate || '0'}
                                            InputProps={{
                                                readOnly: true,
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                                            }}
                                            disabled={submitted}
                                            sx={{
                                                '& .MuiInputBase-input': {
                                                    fontWeight: 600,
                                                    backgroundColor: '#f5f5f5'
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Customer Name"
                                            value={item.customerName || ''}
                                            onChange={(e) => handleItemChange(index, 'customerName', e.target.value)}
                                            disabled={submitted}
                                        />
                                    </Grid>

                                    {/* Customer Signature */}
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                                            {item.customerSignature ? (
                                                <>
                                                    <Box
                                                        component="img"
                                                        src={item.customerSignature}
                                                        alt="Customer Signature"
                                                        sx={{
                                                            maxWidth: 200,
                                                            maxHeight: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 1,
                                                            cursor: submitted ? 'default' : 'pointer',
                                                            flexGrow: 1
                                                        }}
                                                        onClick={(e) => {
                                                            if (!submitted) {
                                                                e.preventDefault();
                                                                setCurrentSignatureField(`customer${index}`);
                                                                setSignatureModalOpen(true);
                                                            }
                                                        }}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleItemChange(index, 'customerSignature', '');
                                                            }}
                                                        >
                                                            <ClearIcon />
                                                        </IconButton>
                                                    )}
                                                </>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setCurrentSignatureField(`customer${index}`);
                                                        setSignatureModalOpen(true);
                                                    }}
                                                    fullWidth
                                                    disabled={submitted}
                                                >
                                                    Customer Signature
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    ))}

                    {/* Add Item Button */}
                    {!submitted && (
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="flex-end" mt={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={addItem}
                                    size="small"
                                >
                                    Add Item
                                </Button>
                            </Box>
                        </Grid>
                    )}

                    {/* Photos Section */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Photos {(form.photos || []).length > 0 && `(${form.photos.length}/5)`}
                                </Typography>
                                {!submitted && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<CameraAltIcon />}
                                        onClick={() => setCameraOpen(true)}
                                        disabled={(form.photos || []).length >= 5}
                                    >
                                        Add Photo
                                    </Button>
                                )}
                            </Box>

                            {(form.photos || []).length > 0 && (
                                <Grid container spacing={2}>
                                    {form.photos.map((photo, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Card variant="outlined">
                                                <Box
                                                    component="img"
                                                    src={photo.dataUrl || photo.url}
                                                    alt={`Photo ${index + 1}`}
                                                    sx={{
                                                        width: '100%',
                                                        maxHeight: 200,
                                                        objectFit: 'cover',
                                                        display: 'block'
                                                    }}
                                                />
                                                <Box sx={{ p: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Description (optional)"
                                                        value={photo.description || ''}
                                                        onChange={(e) => updatePhotoDescription(index, e.target.value)}
                                                        disabled={submitted}
                                                        multiline
                                                        maxRows={2}
                                                    />
                                                    {!submitted && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => removePhoto(index)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Card>
                    </Grid>

                    {/* Submitted By Section */}
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                                Submitted By
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Employee Name"
                                        value={form.submittedBy || ''}
                                        onClick={() => !submitted && setEmployeeModalOpen(true)}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setEmployeeModalOpen(true)} size="small">
                                                        <PersonSearchIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        error={!!errors.submittedBy}
                                        helperText={errors.submittedBy}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                                        {form.submittedBySignature ? (
                                            <>
                                                <Box
                                                    component="img"
                                                    src={form.submittedBySignature}
                                                    alt="Signature"
                                                    sx={{
                                                        maxWidth: 200,
                                                        maxHeight: 60,
                                                        border: '1px solid #ddd',
                                                        borderRadius: 1,
                                                        cursor: submitted ? 'default' : 'pointer',
                                                        flexGrow: 1
                                                    }}
                                                    onClick={(e) => {
                                                        if (!submitted) {
                                                            e.preventDefault();
                                                            setCurrentSignatureField('submittedBy');
                                                            setSignatureModalOpen(true);
                                                        }
                                                    }}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleFieldChange('submittedBySignature', '');
                                                        }}
                                                    >
                                                        <ClearIcon />
                                                    </IconButton>
                                                )}
                                            </>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DrawIcon />}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCurrentSignatureField('submittedBy');
                                                    setSignatureModalOpen(true);
                                                }}
                                                fullWidth
                                                disabled={submitted}
                                            >
                                                Sign
                                            </Button>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Fixed Submit Button */}
            {!submitted && (
                <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 2, flexShrink: 0 }}>
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={saving}
                            sx={{ minWidth: 200 }}
                        >
                            {saving ? 'Submitting...' : 'Submit'}
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Modals */}
            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => {
                    setSignatureModalOpen(false);
                    setCurrentSignatureField(null);
                }}
                sigPadRef={sigPadRef}
                employeeName={
                    currentSignatureField === 'submittedBy'
                        ? form.submittedBy
                        : currentSignatureField?.startsWith('customer')
                            ? form.items[parseInt(currentSignatureField.replace('customer', ''))]?.customerName
                            : ''
                }
                onAccept={handleSignatureAccept}
                existingSignature={
                    currentSignatureField === 'submittedBy'
                        ? form.submittedBySignature
                        : currentSignatureField?.startsWith('customer')
                            ? form.items[parseInt(currentSignatureField.replace('customer', ''))]?.customerSignature
                            : null
                }
            />

            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleEmployeeSelect}
                employees={employees}
            />

            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handlePhotoCapture}
            />
        </Box>
    );
};

export default ChangeOrderForm;
